/** @flow */

import React from "react";
import Loadable from "react-loadable";
import { Loader } from "@components";

const Loading = () => <Loader />;
const defaultConfig = {
  loading: Loading,
  delay: 300,
};
const loadAsync = (fn: Function) => Loadable(Object.assign({}, defaultConfig, { loader: fn }));

const availablePages = {
  HomePage: () => import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/home-page"),
  GoodShapeProductsPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/goodshape-products-page"),
  ServiceHubPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/service-hub-page"),
  AppPromotionPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/app-promotion"),
  MyAbsencesPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/profile/pages/my-absences"),
  AdviceLibraryPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/advice-library"),
  ServiceDirectoryPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/service-directory"),
  ServiceDeskPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/home/pages/service-desk"),

  AbsenceDetailsPage: () =>
    import(/* webpackChunkName: "EmployeeModule" */ "@modules/employee/pages/absence-details"),
  ClientDetailsPage: () =>
    import(/* webpackChunkName: "ClientModule" */ "@modules/client/pages/client-details"),
  SlaPerformancePage: () =>
    import(/* webpackChunkName: "ClientModule" */ "@modules/client/pages/sla-performance"),
  ClientListPage: () =>
    import(/* webpackChunkName: "ClientModule" */ "@modules/client/pages/client-list"),
  DataPage: () => import(/* webpackChunkName: "DataModule" */ "@modules/data/pages/data"),
  DynamicQuestionDetailsPage: () =>
    import(
      /* webpackChunkName: "ServiceModule" */ "@modules/service/pages/dynamic-question-details"
    ),
  HierarchyAdminPage: () =>
    import(/* webpackChunkName: "HierarchyModule" */ "@modules/hierarchy/pages/hierarchy"),
  HierarchyChanges: () =>
    import(/* webpackChunkName: "HierarchyModule" */ "@modules/hierarchy/pages/hierarchy-changes"),

  HierarchyChangeLog: () =>
    import(/* webpackChunkName: "HierarchyModule" */ "@modules/hierarchy/pages/change-log"),
  WellbeingServicesPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/wellbeing-services"),
  NotificationsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/notifications"),
  SecurityPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/security"),

  MyAbsenceStatistics: () =>
    import(/* webpackChunkName: "ProfileModule" */ "@modules/profile/pages/my-absence-statistics"),
  MyDetailsPage: () =>
    import(/* webpackChunkName: "ProfileModule" */ "@modules/profile/pages/my-details"),

  MyEmployees: () =>
    import(/* webpackChunkName: "ProfileModule" */ "@modules/profile/pages/my-employees"),
  MyEmployeeDetails: () =>
    import(/* webpackChunkName: "ProfileModule" */ "@modules/profile/pages/my-employee-details"),
  RtwDetailsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/rtw-details"),
  AbsenceMonitoringDetailsPage: () =>
    import(
      /* webpackChunkName: "ServiceModule" */ "@modules/service/pages/absence-monitoring-details"
    ),
  ReferralDetailsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/referral-details"),
  ReferralsOverviewPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/reporting/pages/referrals-overview"),
  NewReferralsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/reporting/pages/new-referrals"),
  ActiveReferralsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/reporting/pages/active-referrals"),
  ClosedReferralsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/reporting/pages/closed-referrals"),
  OutcomesReportPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/reporting/pages/outcomes-report"),
  ManagerKPI: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/reporting/pages/manager-kpi"),
  SettingsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/settings"),
  ServiceUnavailable: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/account/pages/service-unavailable"),
  FeaturesPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/features"),
  ServiceAlertDetailsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/service-alert-details"),
  StandardQuestionDetailsPage: () =>
    import(
      /* webpackChunkName: "ServiceModule" */ "@modules/service/pages/standard-question-details"
    ),
  DashboardCoronavirusPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/dashboard-coronavirus"
    ),
  DashboardIndexPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/dashboard-index"),
  DashboardOverviewPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/dashboard-overview"),
  DataForecastsPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/data-forecasts-page"
    ),
  DashboardActivityFeedPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/dashboard-activityfeed"
    ),
  ReportingTasksPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/tasks-page"),
  ReportingReportsOverviewPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-overview"),
  AbsenceDetailsOverviewPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/absence-details-overview"
    ),
  ReportingMyNotificationsPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/my-notifications"),
  ReportsAbsenceMonitoringDetailsOverviewPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-absence-monitoring-details-overview"
    ),
  ReportsRtwDetailsOverview: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-rtw-details-overview"
    ),
  AbsenceMonitoringDetailsReport: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-absence-monitoring-details-report"
    ),
  ReportsRtwDetailsReport: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-rtw-details-report"
    ),
  ReportsAlertsDetailsPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-alerts-details-page"
    ),
  AlertDetailsReport: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/reports-alert-details-report"
    ),
  AbsenceDetailsReportPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/absence-details-report"
    ),
  FocusListsEmployeeOverviewPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/focus-lists-employee-overview"
    ),
  FocusListsEmployeeReport: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/focus-lists-employee-report"
    ),
  FocusListsManagerOverviewPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/focus-lists-manager-overview"
    ),
  ReportingFocusListsManagerReportPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/focus-lists-manager-report"
    ),
  FocusListsDepartmentOverviewPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/focus-lists-department-overview"
    ),
  FocusListsDepartmentReport: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/focus-lists-department-report"
    ),
  TrendAbsenceRatePage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/trends-absence-rates"
    ),
  TrendsAbsenceRatesReport: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/trends-absence-rates-report"
    ),
  TrendsCompliancePage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/trends-compliance"),
  TrendsComplianceReportPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/trends-compliance-report"
    ),
  TrendsAbsenceReasonsPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/trends-absence-reasons"
    ),
  TrendAbsenceReasonReportPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/trends-absence-reasons-report"
    ),
  EmployeeSearchPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-search"),
  EmployeeMonitoringPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-monitoring"
    ),
  EmployeesAbsencesPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employees-absences-page"
    ),
  WellbeingReferralsPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/wellbeing-referrals"
    ),
  EmployeeStatisticsPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-statistics"
    ),
  EmployeeNotesPage: () =>
    import(
      /* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-notes-page"
    ),
  ReportingEmployeeForecasts: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-forecasts"),
  ReportingEmployeeReferralsPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-referrals"),
  MiReports: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/mi-reports"),
  EmployeeDocumentsPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/employee-documents"),
  EmployeeDetailsPage: () =>
    import(/* webpackChunkName: "EmployeeModule" */ "@modules/reporting/pages/employee-details"),

  // Help
  HelpPage: () => import(/* webpackChunkName: "HelpModule" */ "@modules/help/pages/help"),
  MobileAppPage: () =>
    import(/* webpackChunkName: "MobileAppModule" */ "@modules/mobile-app/pages/main"),
  UsersPage: () => import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/users"),

  // Health Reports
  HealthOverviewPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/health-overview"),
  WorkForceHealthPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/work-force"),
  CarePlansPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/care-plans"),
  WellbeingAdvicePage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/wellbeing-advice"),
  NurseAssessmentsPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/nurse-assessments"),
  ProvidersPage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/providers"),
  KpiCompliancePage: () =>
    import(/* webpackChunkName: "ReportingModule" */ "@modules/reporting/pages/kpi-compliance"),

  // Audit Logs
  AuditLogsPage: () =>
    import(/* webpackChunkName: "ServiceModule" */ "@modules/service/pages/audit-logs"),

  NoAccessPage: () =>
    import(/* webpackChunkName: "AccountModule" */ "@modules/account/pages/no-access"),
  Error404: () => import(/* webpackChunkName: "Error404" */ "@modules/client/pages/error-404"),
  /*eslint-enable*/
};

let pages: { [key: $Keys<typeof availablePages>]: any } = {};

for (const component of Object.keys(availablePages)) {
  // Register all pages
  pages[component] = loadAsync(availablePages[component]);
}

export default pages;
